import {Component, Inject, OnDestroy, OnInit} from '@angular/core';
import {EMPTY, Observable, Subject} from 'rxjs';
import {debounceTime, startWith, takeUntil, tap} from 'rxjs/operators';
import {ActivatedRoute, Params} from '@angular/router';
import {APP_GUIDS, AppGuids} from '../../../app.guids';
import {FormControl} from '@angular/forms';
import {Store} from '@ngrx/store';
import {ProleisSuppliersState} from '../../store/store.index';
import {OrdersActions, OrdersSelectors,} from '../../store/orders/orders.index';
import {FileResponse} from '../../../models/file.response';
import {saveAs} from 'file-saver';
import {OrdersOverview} from '../../store/orders/orders.state';
import {SearchPositionDialogComponent} from '../search-position/search.position.dialog.component';
import {MatDialog} from '@angular/material/dialog';
import {CoreService, DocumentService, ProleisObject} from "@proleis/proleis-rest-client";

@Component({
  selector: 'ps-order-overview',
  templateUrl: 'order.overview.component.html',
  styleUrls: ['order.overview.component.scss'],
})
export class OrderOverviewComponent implements OnInit, OnDestroy {
  destroy$: Subject<any> = new Subject<any>();
  overview$: Observable<OrdersOverview>;
  overviewType: string;
  overviewIsLoading$: Observable<boolean>;
  selectedIndex$: Observable<number>;
  params: Params;
  searchFormControl: FormControl;
  search: boolean;
  displayAsList = false;

  constructor(
    public activatedRoute: ActivatedRoute,
    private documentsService: DocumentService,
    private matDialog: MatDialog,
    private coreService: CoreService,
    @Inject(APP_GUIDS) private guids: AppGuids,
    private store: Store<ProleisSuppliersState>,
  ) {}

  ngOnInit() {
    this.initFilter();
    this.displayAsList = Boolean(localStorage.getItem('displayAsList'));
    this.activatedRoute.url.subscribe((url) => {
      this.params = this.activatedRoute.snapshot.params;

      this.store.dispatch(
        OrdersActions.LOAD_ORDER({ orderId: this.params.id }),
      );
      this.store
        .select(OrdersSelectors.selectOrder)
        .pipe(takeUntil(this.destroy$))
        .subscribe((order) => {
          if (order) {
            let overviewType = '';
            switch (order.CHANNELGUID) {
              case this.guids.GUID_CHANNEL_EXTERNAL:
                overviewType = 'ext';
                break;
              case this.guids.GUID_CHANNEL_INTERNAL:
                overviewType = 'int';
                break;
              case this.guids.GUID_CHANNEL_PM:
                overviewType = 'pm';
                break;
            }
            this.overviewType = overviewType;
            this.store.dispatch(
              OrdersActions.LOAD_OVERVIEW({
                orderId: this.params.id,
                overviewType,
              }),
            );
          }
        });

      this.overview$ = this.store.select(OrdersSelectors.SELECT_OVERVIEW);
      this.overviewIsLoading$ = this.store.select(
        OrdersSelectors.SELECT_OVERVIEW_IS_LOADING,
      );
      this.selectedIndex$ = this.store.select(
        OrdersSelectors.selectSelectedTabIndex,
      );
      this.searchFormControl.valueChanges
        .pipe(
          tap((value) =>
            localStorage.setItem('ps-orders-orders-overview-filter', value),
          ),
          debounceTime(250),
          startWith(localStorage.getItem('ps-orders-orders-overview-filter')),
          takeUntil(this.destroy$),
        )
        .subscribe((filter) => {
          if (filter) {
            this.store.dispatch(
              OrdersActions.FILTER_OVERVIEW({
                filter,
                overview$: this.overview$,
              }),
            );
          }
        });
    });
  }

  reload() {
    this.store.dispatch(OrdersActions.LOAD_ORDER({ orderId: this.params.id }));
  }

  ngOnDestroy() {
    this.destroy$.next(EMPTY);
  }

  checkExternal(order: ProleisObject): boolean {
    return order.CHANNELGUID !== this.guids.GUID_CHANNEL_INTERNAL;
  }

  checkPm(order: ProleisObject): boolean {
    return order.CHANNELGUID === this.guids.GUID_CHANNEL_PM;
  }

  initFilter() {
    this.searchFormControl = new FormControl();

    const filterValue = localStorage.getItem(
      'ps-orders-orders-overview-filter',
    );
    if (filterValue) {
      this.searchFormControl.setValue(filterValue);
    }
  }

  toggleSearch() {
    this.search = !this.search;
  }

  downloadProjectStatusReport(order: ProleisObject) {
    let actionId = '';
    if (this.checkExternal(order)) {
      actionId = '§' + this.guids.GUID_SCRIPT_PBO_PROJECT_INFO_EXTERN;
    } else {
      actionId = '§' + this.guids.GUID_SCRIPT_PBO_PROJECT_INFO_INTERN;
    }

    this.coreService
      .executeAction<any>(this.params.id, actionId)
      .subscribe((result) => {
        this.documentsService
          .getDocumentFile(result.value)
          .subscribe((response) => {
            const fileResponse: FileResponse =
              FileResponse.parseResponse(response);
            saveAs(fileResponse.content, fileResponse.fileName);
          });
      });
  }

  onSelectedTabChange(event) {
    this.store.dispatch(OrdersActions.SELECT_TAB_INDEX({ index: event }));
  }

  openSearchDialog() {
    this.matDialog.open(SearchPositionDialogComponent);
  }

  toggleDisplay() {
    this.displayAsList = !this.displayAsList;
    localStorage.setItem('displayAsList', String(this.displayAsList));
  }
}
